import { useSyncExternalStore } from 'react';

/**
 * Калибровка визуала слушателя по РЕАЛЬНО СЛЫШИМЫМ кликам метронома.
 *
 * Проблема: доли на экране слушателя привязаны к wallStart, а звук доходит
 * через сеть + джиттер-буфер (50–150 мс, меняется на лету) — визуал бежит
 * впереди звука. Статистика RTC это приближённо, а здесь точно:
 *
 *  - удалённый трек микса подключается тапом (MediaStreamSource → AnalyserNode)
 *    — на сам звук это НЕ влияет, тап не перехватывает элемент;
 *  - по фронтам кликов (превышение RMS в 4 раза над фоном, мин. дистанция
 *    0.4·интервала) находится реальное время доли в наушниках слушателя;
 *  - остаток относительно ожидаемой сетки битов сглаживается (EMA α=0.1) —
 *    получаем оценку полной задержки звука D (сеть + джиттер + декодер);
 *  - визуал рисует долю k в момент Date.now() = wallStart + k·interval + D,
 *    т.е. ровно тогда, когда клик прозвучал.
 *
 * Для музыки без чётких фронтов кликов не будет — D остаётся на последнем
 * значении (или на начальной оценке scheduleDelayMs).
 */
export class HeardBeatCalibrator {
  constructor() {
    this.ctx = null;
    this.taps = new Map(); // key -> { analyser, buf, levelEma, lastOnsetMs, identity }
    this.grid = null; // { from, wallStartLocal, bpm, intervalMs, initialDelayMs }
    this.delayMs = 0; // оценка полной задержки звука D
    this.listeners = new Set();
    this.loopId = null;
    // Подхват ступенчатой смены задержки (перестройка джиттер-буфера):
    // фронты систематически отвергаются, но кластеризуются в одной точке
    this.rejectedCluster = { residual: null, count: 0 };
  }

  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getDelayMs = () => this.delayMs;

  /** Реактивный доступ к оценке задержки D (мс) для визуала. */
  useDelay = () => useSyncExternalStore(this.subscribe, this.getDelayMs, this.getDelayMs);

  ensureContext() {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      this.ctx = new Ctx();
    }
    // Автоплей-политика: резюмим при каждом удобном случае, пока не взлетит
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  /**
   * Подключает тап анализатора к удалённому аудиотреку.
   * На воспроизведение не влияет: тап идёт параллельным потребителем трека.
   */
  tapTrack(track, key, identity) {
    if (!track?.mediaStreamTrack || this.taps.has(key)) return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    const source = ctx.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    // Узел без пути к destination не «пуллится» браузером —
    // глушим нулевым gain, но оставляем в графе
    const sink = ctx.createGain();
    sink.gain.value = 0;
    analyser.connect(sink);
    sink.connect(ctx.destination);
    this.taps.set(key, {
      analyser,
      identity,
      buf: new Float32Array(analyser.fftSize),
      levelEma: 0,
      lastOnsetMs: 0,
    });
    this.startLoop();
  }

  untap(key) {
    const tap = this.taps.get(key);
    if (!tap) return;
    tap.analyser.disconnect();
    this.taps.delete(key);
    if (this.taps.size === 0) this.stopLoop();
  }

  untapAll() {
    [...this.taps.keys()].forEach((key) => this.untap(key));
  }

  /**
   * Задаёт сетку битов для калибровки (переведённые в локальные часы часы
   * ведущего). null — калибровка неактивна.
   */
  setGrid(grid) {
    this.grid = grid
      ? { ...grid, intervalMs: (60 / grid.bpm) * 1000 }
      : null;
    if (grid) {
      // Начальная оценка D — запас планирования старта из сообщения
      this.setDelay(grid.initialDelayMs ?? 250);
      this.ensureContext();
    }
  }

  setDelay(delayMs) {
    const clamped = Math.max(0, Math.min(3000, delayMs));
    if (Math.abs(clamped - this.delayMs) < 0.5) return;
    this.delayMs = clamped;
    this.listeners.forEach((l) => l());
  }

  /** Обработка одного фронта клика (вынесено отдельно для тестируемости). */
  handleOnset(timeMs) {
    if (!this.grid) return;
    const { wallStartLocal, intervalMs } = this.grid;
    // ближайшая доля при текущей оценке задержки
    const k = Math.round((timeMs - wallStartLocal - this.delayMs) / intervalMs);
    if (k < 0) return;
    const residual = timeMs - (wallStartLocal + k * intervalMs); // ≈ D_true при верном k
    // Фронт должен попасть в окрестность текущей оценки: голос/музыка
    // (равномерно по фазе) за пределами ±0.3·interval отбрасываются
    if (Math.abs(residual - this.delayMs) > intervalMs * 0.3) {
      this.trackRejectedResidual(residual, intervalMs);
      return;
    }
    this.rejectedCluster = { residual: null, count: 0 };
    // EMA α=0.1 — медленно сходится (~20 кликов), устойчиво к джиттеру
    this.setDelay(this.delayMs + 0.1 * (residual - this.delayMs));
  }

  /**
   * Ступенчатое изменение задержки (сеть/буфер): фронты стабильно приходят
   * вне допуска, но все в одной точке — быстро прыгаем к ней (α=0.3).
   * Размазанные отвержения (голос) кластер не образуют.
   */
  trackRejectedResidual(residual, intervalMs) {
    const cluster = this.rejectedCluster;
    if (cluster.residual === null || Math.abs(residual - cluster.residual) > intervalMs * 0.12) {
      this.rejectedCluster = { residual, count: 1 };
      return;
    }
    cluster.count += 1;
    if (cluster.count >= 3) {
      this.setDelay(this.delayMs + 0.3 * (cluster.residual - this.delayMs));
      this.rejectedCluster = { residual: null, count: 0 };
    }
  }

  startLoop() {
    if (this.loopId !== null || typeof window === 'undefined') return;
    this.loopId = window.setInterval(() => this.tick(), 25);
  }

  stopLoop() {
    if (this.loopId !== null) {
      window.clearInterval(this.loopId);
      this.loopId = null;
    }
  }

  tick() {
    if (!this.grid) return;
    const nowMs = Date.now();
    this.taps.forEach((tap, key) => {
      if (tap.identity !== this.grid.from) return; // калибруемся по треку ведущего
      tap.analyser.getFloatTimeDomainData(tap.buf);
      let sum = 0;
      for (let i = 0; i < tap.buf.length; i++) sum += tap.buf[i] * tap.buf[i];
      const rms = Math.sqrt(sum / tap.buf.length);
      tap.levelEma = tap.levelEma * 0.95 + rms * 0.05;
      const minSepMs = this.grid.intervalMs * 0.4;
      const isOnset =
        rms > Math.max(4 * tap.levelEma, 0.02) &&
        nowMs - tap.lastOnsetMs > minSepMs;
      if (isOnset) {
        tap.lastOnsetMs = nowMs;
        this.handleOnset(nowMs);
      }
      if (this.taps.get(key) !== tap) return;
    });
  }
}

export const heardBeatCalibrator = new HeardBeatCalibrator();

/** Оценка полной задержки звука D (мс) для визуала слушателя. */
export const useHeardDelayMs = () => heardBeatCalibrator.useDelay();
